import { AppBar, Toolbar } from '@material-ui/core';
import { EventType } from 'common-event';
import React, { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'common-react';
import { Currency, Button, Typography, Icon, formatCurrency } from 'ui-components';
import { ProductType, isKorpoRegisterMode } from 'cart-model';
import { AmountValidationError } from '../../SetAmountsPerFund/cartValidation';
import { ReactComponent as AlertIcon } from 'common-assets/assets/icons/alert.svg';
import { logEvent, logSubmitSetAmounts } from '../../../model/events';
import { urlToDataWizard } from '../../../model/integration';
import ProductWizardState from '../../../model/ProductWizardState';
import Screen from '../../ProductWizard/Screen';

import useStyles from './CartSumBar.style';

const CartSumBar: FunctionComponent<{
  nextEnabled: boolean
  onNextClick?: () => void;
  showSum?: boolean;
  productTypeToValidationError?: Record<ProductType, AmountValidationError>;
  sumError?: boolean;
  backButtonText?: string;
}> = ({ nextEnabled, onNextClick, showSum = true, productTypeToValidationError, sumError, backButtonText }) => {
  const [t] = useTranslation('cart');
  const [urlT] = useTranslation('url');
  const c = useStyles({});
  const cartFunds = useSelector((state: ProductWizardState) => {
    return state.cartState.cartFunds;
  });
  const sum = cartFunds.reduce((s, cartFund) => s + cartFund.amount, 0);
  const history = useHistory(urlT);

  const config = useSelector((state: ProductWizardState) => state.config);
  const cartFundsByProductType = cartFunds.reduce<Record<string, typeof cartFunds>>((acc, cf) => {
    const key = (cf.productType || '').toString();
    acc[key] = acc[key] || [];
    acc[key].push(cf);
    return acc;
  }, {});
  const productTypes = Object.keys(cartFundsByProductType);
  const registerMode = useSelector((state: ProductWizardState) => state.cartState.registerMode);
  const isKorpo = isKorpoRegisterMode(registerMode);
  const singleProductTypeLabel = useMemo(() => {
    if (productTypes.length !== 1) return undefined;
    const baseKey = productTypes[0].toLowerCase();
    const productKey = `${baseKey}${isKorpo ? '_korpo' : ''}`;
    return t('cart-item.purchase-summary-label', { productType: t(`selected-product-type.${productKey}`) });
  }, [productTypes, isKorpo, t]);

  const handleNextClick = () => {
    onNextClick && onNextClick();
    logSubmitSetAmounts();
  };


  const targetUrl = config.dataWizardEnabled
  ? '/data-wizard'
  : urlToDataWizard();

  const handleClick = () => {
    handleNextClick();
    window.location.href = targetUrl;
  };

  const ikeError = productTypeToValidationError?.[ProductType.ike];
  const ikzeError = productTypeToValidationError?.[ProductType.ikze];
  const error = (ikzeError?.type === 'max' && ikzeError) || (ikeError?.type === 'max' && ikeError);
  const errorYear = error ? new Date().getFullYear() : undefined;

  return (
    <AppBar position="fixed" color="primary" className={c.appBar}>
      <Toolbar>
        <div className={c.container}>
          <div className={c.backButtonWrapper}>
            <Button
              buttonType="secondary"
              onClick={() => {
                logEvent(EventType.setquotaBack);
                history.push(Screen.SELECT_FUNDS.code);
              }}
            >
              {backButtonText || t('to-select-funds')}
            </Button>
          </div>
          {showSum && <div  className={c.sumWrapper}>
            <div className={c.sumTitleRow}>
              <Typography textColor="greys.60">
                {singleProductTypeLabel ?? t('box.total')}
              </Typography>
              {error && (
                <div className={c.sumErrorMessage}>
                  <Icon className={c.alertIcon}><AlertIcon /></Icon>
                  <Typography variant="body2" className={c.sumErrorText}>
                    {t('box.limit_exceeded_year', { year: errorYear, amount: formatCurrency(error.max || 0) })}
                  </Typography>
                </div>
              )}
            </div>
            <Currency
              value={sum}
              valueSize="subHeadline2"
              codeSize="subHeadline2"
              valueFontWeight="bold"
              valueClassName={sumError ? c.sumErrorValue : undefined}
              codeClassName={sumError ? c.sumErrorValue : undefined}
            />
          </div>}
          <div className={c.nextButtonWrapper}>
            <Button
              accent={true}
              disabled={!nextEnabled}
              onClick={handleClick}
            >
              {t('next')}
            </Button>
          </div>
        </div>
      </Toolbar>
    </AppBar>
  );
};

export default CartSumBar;
